/**
 * Top-centre scrolling compass strip.
 *
 * North is world -Z. Bearings for objectives ride the strip and clamp to the
 * ends with a fade so you always know which way to turn. One canvas, redrawn
 * only when the heading moves more than a fifth of a degree.
 */
import { COL, rgba, clamp, fitCanvas, clearCanvas, devicePx, crispRect, chamferPath } from './Theme.js';
import { drawGlyphs, label } from './Glyphs.js';

export const W = 660, H = 86;
const SPAN_DEG = 132;                 // total arc visible across the strip
const PPD = W / SPAN_DEG;             // px per degree

const CARDINAL = [
  [0, 'N', 1], [45, 'NE', 0], [90, 'E', 1], [135, 'SE', 0],
  [180, 'S', 1], [225, 'SW', 0], [270, 'W', 1], [315, 'NW', 0],
];

export function headingFromYaw(yaw) {
  let d = (-yaw * 180) / Math.PI;
  d %= 360;
  if (d < 0) d += 360;
  return d;
}

export function bearingTo(dx, dz) {
  let d = (Math.atan2(dx, -dz) * 180) / Math.PI;
  if (d < 0) d += 360;
  return d;
}

function delta(a, b) {
  let d = a - b;
  while (d > 180) d -= 360;
  while (d < -180) d += 360;
  return d;
}

export class Compass {
  constructor(root) {
    this.cv = document.createElement('canvas');
    this.cv.className = 'hud-c hud-compass';
    root.appendChild(this.cv);
    this.g = fitCanvas(this.cv, W, H);
    this._last = -999;
    this.dirty = true;
  }

  resize(dpr = devicePx()) {
    this.g = fitCanvas(this.cv, W, H, dpr);
    this._last = -999;
    this.dirty = true;
    this._buildTape(dpr);
    this._fadeL = this._fadeR = null;
  }

  /**
   * The letters + tick rail never change, only their offset. Bake the full 360°
   * once and blit a window of it each frame; that turns ~130 fills per frame
   * into two drawImage calls.
   */
  _buildTape(dpr) {
    const tw = Math.round(360 * PPD);
    const th = 40;                       // canvas band the tape occupies
    const cv = this._tape || (this._tape = document.createElement('canvas'));
    cv.width = Math.round(tw * dpr);
    cv.height = Math.round(th * dpr);
    const g = cv.getContext('2d');
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, tw, th);
    g.__dpr = dpr;
    this.tapeW = tw;
    this.tapeH = th;

    // Tape space: x = degrees * PPD, with 0° at x = 0. Band-local y:
    //   0-16 letters, 21-30 tick rail.
    for (const [deg, name, primary] of CARDINAL) {
      const x = deg * PPD;
      for (const ox of [-tw, 0, tw]) {   // wrap the glyph across the seam
        label(g, name, x + ox, 0, {
          size: primary ? 16 : 11,
          weight: primary ? 700 : 600,
          tracking: 0.10,
          align: 'center',
          color: primary ? COL.white : rgba(COL.bone, 0.86),
        });
      }
    }
    for (let d = 0; d < 360; d += 5) {
      const x = d * PPD;
      const major = d % 45 === 0;
      const mid = d % 15 === 0;
      const h = major ? 9 : mid ? 6 : 3;
      g.fillStyle = rgba('#000000', 0.5);
      crispRect(g, x - 1, 20, 3, h + 1);
      g.fillStyle = rgba(COL.white, major ? 0.92 : mid ? 0.5 : 0.26);
      crispRect(g, x - 0.5, 20, 1, h);
    }
  }

  needsRedraw(heading) {
    return this.dirty || Math.abs(delta(heading, this._last)) > 0.18;
  }

  draw(heading, markers) {
    const g = this.g;
    this._last = heading;
    this.dirty = false;
    clearCanvas(g);

    const cx = W / 2;
    // Vertical bands, top to bottom:
    //   0-18 heading chip · 18-26 centre index · 28-44 cardinal letters
    //   48-58 tick rail   · 60-84 objective chips hanging below.
    const railY = 48;

    /* backing: a soft dark wash so the strip reads against a bright sky */
    if (!this._wash) {
      const grad = g.createLinearGradient(0, 26, 0, 56);
      grad.addColorStop(0, 'rgba(4,7,10,0.00)');
      grad.addColorStop(0.5, 'rgba(4,7,10,0.44)');
      grad.addColorStop(1, 'rgba(4,7,10,0.00)');
      this._wash = grad;
    }
    g.fillStyle = this._wash;
    g.fillRect(0, 26, W, 30);

    /* -------------------------------- cardinal letters + tick rail (tape) */
    if (this._tape) {
      // Window into the baked tape, wrapped across the 0°/360° seam.
      const dpr = g.__dpr;
      const tw = this.tapeW;
      let sx = ((heading - SPAN_DEG / 2) * PPD) % tw;
      if (sx < 0) sx += tw;
      // Snap the source offset to a whole device pixel: a fractional source rect
      // makes drawImage resample, and a resampled tick rail goes soft.
      sx = Math.round(sx * dpr) / dpr;
      const dstY = railY - 20;               // tape band y=20 is the rail top
      const first = Math.min(W, tw - sx);
      g.drawImage(this._tape, Math.round(sx * dpr), 0,
        Math.round(first * dpr), Math.round(this.tapeH * dpr),
        0, dstY, first, this.tapeH);
      if (first < W) {
        g.drawImage(this._tape, 0, 0,
          Math.round((W - first) * dpr), Math.round(this.tapeH * dpr),
          first, dstY, W - first, this.tapeH);
      }
    }

    /* -------------------------------------------------- objective markers */
    if (markers) {
      for (const m of markers) {
        const d = delta(m.bearing, heading);
        const clampEdge = SPAN_DEG / 2 - 24;  // keep chips clear of the edge fade
        const clamped = Math.abs(d) > clampEdge;
        const x = cx + clamp(d, -clampEdge, clampEdge) * PPD;
        const col = m.color || COL.amber;
        const a = clamped ? 0.55 : 1;
        g.save();
        g.globalAlpha = a;
        // stem hanging off the strip
        g.fillStyle = rgba(col, 0.7);
        crispRect(g, x - 0.5, 58, 1, 6);
        // chamfered chip
        chamferPath(g, x - 11, 63, 22, 20, 5);
        g.fillStyle = rgba('#04070A', 0.86);
        g.fill();
        g.lineWidth = 1;
        g.strokeStyle = rgba(col, 0.9);
        g.stroke();
        label(g, m.label, x, 67, { size: 12, weight: 700, tracking: 0.02, align: 'center', color: col, halo: null });
        if (clamped) {
          // arrow showing which way it is really off to
          g.beginPath();
          const s = Math.sign(d);
          g.moveTo(x + s * 20, 73);
          g.lineTo(x + s * 13, 68);
          g.lineTo(x + s * 13, 78);
          g.closePath();
          g.fillStyle = rgba(col, 0.9);
          g.fill();
        }
        g.restore();
      }
    }

    /* -------------------------------------------------- heading  readout */
    const deg = Math.round(heading) % 360;
    const txt = String(deg).padStart(3, '0');
    chamferPath(g, cx - 27, 0, 54, 18, 5);
    g.fillStyle = rgba('#04070A', 0.80);
    g.fill();
    g.lineWidth = 1;
    g.strokeStyle = rgba(COL.white, 0.20);
    g.stroke();
    drawGlyphs(g, txt, cx, 3, 12, {
      align: 'center', color: COL.white, weight: 0.19, tracking: 0.16, halo: null,
    });

    /* ------------------------------------------------------- centre index */
    g.fillStyle = rgba('#000000', 0.65);
    g.beginPath();
    g.moveTo(cx, 27); g.lineTo(cx - 7, 18); g.lineTo(cx + 7, 18);
    g.closePath(); g.fill();
    g.fillStyle = COL.amber;
    g.beginPath();
    g.moveTo(cx, 25.5); g.lineTo(cx - 5.4, 19); g.lineTo(cx + 5.4, 19);
    g.closePath(); g.fill();

    /* ------------------------------------------------------- edge fade */
    const fadeW = 96;
    if (!this._fadeL) {
      let fg = g.createLinearGradient(0, 0, fadeW, 0);
      fg.addColorStop(0, 'rgba(0,0,0,1)'); fg.addColorStop(1, 'rgba(0,0,0,0)');
      this._fadeL = fg;
      fg = g.createLinearGradient(W - fadeW, 0, W, 0);
      fg.addColorStop(0, 'rgba(0,0,0,0)'); fg.addColorStop(1, 'rgba(0,0,0,1)');
      this._fadeR = fg;
    }
    g.save();
    g.globalCompositeOperation = 'destination-out';
    g.fillStyle = this._fadeL; g.fillRect(0, 0, fadeW, H);
    g.fillStyle = this._fadeR; g.fillRect(W - fadeW, 0, fadeW, H);
    g.restore();
  }
}
